import { Button, Switch, Tag, Text } from "@ttc3k/trekker";
import { format, parse } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { useNotesForListingStaffQuery } from "gql/generated";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Collapsible,
  EmptyState,
  NoteCard,
  TimelineGroupLayout,
} from "shared/components";
import { EmailActivitySlideout } from "shared/components/organisms/EmailActivitySlideout/EmailActivitySlideout";
import { SimpleLoader } from "shared/components/atoms";
import { DATE_PATTERN_SIMPLE } from "shared/utils";
import { Flex } from "styled-system/jsx";
import { Variants } from "framer-motion";
import { SystemEventRow } from "./SystemEventRow";
import { StaffNoteRow } from "./StaffNoteRow";
import {
  OperatorTimelineFilter,
  useOperatorNotesTimeline,
  type RenewalEmailActivityLog,
} from "./useOperatorNotesTimeline";

export type OperatorNotesTimelineProps = {
  operatorEntityId: string;
  organizationId?: string;
  suggestionsPortalHost?: HTMLElement | null;
};

const formatGroupDate = (groupByDate: string, todayLabel: string) => {
  const itemDate = format(
    parse(groupByDate, DATE_PATTERN_SIMPLE, new Date()),
    DATE_PATTERN_SIMPLE,
  );
  const today = format(new Date(), DATE_PATTERN_SIMPLE);
  return itemDate === today
    ? todayLabel
    : format(toZonedTime(groupByDate, "UTC"), "MMM d");
};

export const OperatorNotesTimeline = ({
  operatorEntityId,
  organizationId,
  suggestionsPortalHost,
}: OperatorNotesTimelineProps) => {
  const { t } = useTranslation("directory", {
    keyPrefix: "OPERATOR.FLYOUT.TIMELINE",
  });
  const { t: tCore } = useTranslation("core");

  const [showStaff, setShowStaff] = useState(false);
  const [selectedRenewalEmail, setSelectedRenewalEmail] =
    useState<RenewalEmailActivityLog | null>(null);

  const { data: staffData, loading: staffLoading, error: staffError } =
    useNotesForListingStaffQuery({
      variables: {
        operatorEntityId,
        organizationId: organizationId!,
      },
      skip: !showStaff || !organizationId,
    });

  const {
    filterOptions,
    selectedFilter,
    timelineGroups,
    filteredGroups,
    loading,
    notesError,
    handleFilterClick,
    isAddingNote,
    setIsAddingNote,
    handleNoteSaveSuccess,
  } = useOperatorNotesTimeline({
    operatorEntityId,
    organizationId,
    staffNotes: staffData?.notesForListingStaff ?? undefined,
    staffNotesLoading: staffLoading,
  });

  return (
    <Flex flexDir="column" gap="300">
      {/* Add note button */}
      <Button
        visual="unstyled"
        fontSize="16px"
        fontWeight="500"
        color="text.accent.blue.mid"
        _hover={{ color: "text.accent.blue.dark" }}
        _focusVisible={{ color: "text.accent.blue.dark" }}
        onClick={() => setIsAddingNote(true)}
        alignSelf="flex-start"
      >
        {t("CTA_BUTTON_COPY")}
      </Button>

      {/* Filter chips */}
      <Flex alignItems="center" gap="100">
        <Text visual="smallTag" flexShrink={0}>
          {t("ONLY_SHOW.TITLE")}
        </Text>
        <Flex alignItems="center" gap="75">
          {filterOptions
            .filter(f => f.value !== OperatorTimelineFilter.STAFF || showStaff)
            .map(filter => (
              <Tag
                key={filter.value}
                visual="primary"
                mode={selectedFilter === filter.value ? "dark" : "light"}
                onClick={() => handleFilterClick(filter.value)}
              >
                {t(`ONLY_SHOW.${filter.value}`)}
              </Tag>
            ))}
        </Flex>
      </Flex>

      {/* Staff toggle */}
      {organizationId && (
        <Flex alignItems="center" gap="150">
          <Switch
            checked={showStaff}
            onChange={() => {
              setShowStaff(prev => {
                if (prev && selectedFilter === OperatorTimelineFilter.STAFF) {
                  handleFilterClick(OperatorTimelineFilter.STAFF);
                }
                return !prev;
              });
            }}
          />
          <Text visual="smallRegular" color="text.mid">
            {t("SHOW_STAFF_NOTES_TOGGLE")}
          </Text>
        </Flex>
      )}

      {/* New note form */}
      <Collapsible
        open={isAddingNote}
        additionalVariantProps={
          {
            open: { display: "block" },
            closed: { display: "none" },
          } as Variants
        }
      >
        {isAddingNote && (
          <NoteCard
            initialMode="edit"
            onSaveSuccess={handleNoteSaveSuccess}
            onCancel={() => setIsAddingNote(false)}
            suggestionsPortalHost={suggestionsPortalHost}
          />
        )}
      </Collapsible>

      {/* Loading */}
      {loading && <SimpleLoader />}

      {/* Errors */}
      {notesError && (
        <Text visual="smallRegular" color="text.error">
          {t("MESSAGES.ERROR_NOTES")}
        </Text>
      )}
      {staffError && (
        <Text visual="smallRegular" color="text.error">
          {t("MESSAGES.ERROR_STAFF_NOTES")}
        </Text>
      )}

      {/* Empty states */}
      {!loading && !isAddingNote && timelineGroups.length === 0 && (
        <EmptyState text={t("MESSAGES.NO_DATA")} p="400" minH="120px" />
      )}
      {!loading &&
        !isAddingNote &&
        timelineGroups.length > 0 &&
        filteredGroups.length === 0 && (
          <EmptyState text={t("MESSAGES.NO_DATA_FOR_FILTER")} p="400" minH="120px" />
        )}

      {/* Timeline groups */}
      {!loading &&
        !isAddingNote &&
        filteredGroups.map(group => (
          <TimelineGroupLayout
            key={group.groupByDate}
            formattedDateString={formatGroupDate(group.groupByDate, tCore("TODAY"))}
          >
            {group.items.map(item => {
              if (item.filterGroup === OperatorTimelineFilter.SYSTEM) {
                return (
                  <SystemEventRow
                    key={item._id}
                    item={item}
                    onOpenRenewalEmail={setSelectedRenewalEmail}
                  />
                );
              }
              if (item.filterGroup === OperatorTimelineFilter.STAFF) {
                return (
                  <StaffNoteRow
                    key={item._id}
                    item={item}
                    suggestionsPortalHost={suggestionsPortalHost}
                  />
                );
              }
              if (item.note) {
                return (
                  <NoteCard
                    key={item.note._id}
                    initialMode="view"
                    note={item.note}
                    suggestionsPortalHost={suggestionsPortalHost}
                  />
                );
              }
              return null;
            })}
          </TimelineGroupLayout>
        ))}

      <EmailActivitySlideout
        isOpen={!!selectedRenewalEmail}
        onClose={() => setSelectedRenewalEmail(null)}
        activityLog={selectedRenewalEmail}
        zIndex="max"
      />
    </Flex>
  );
};
